import { decorate, injectable } from '@evertel/di'
import { Store } from '@evertel/types'
import { localId } from '@evertel/utils'

/** Identifies the conversation, and optional reply context, a draft belongs to */
interface DraftTarget {
    modelType: 'room' | 'thread'
    modelId: number
    repliesToId?: number | null
}

interface MessageDraft {
    /**
     * Client-generated negative id from localId(). Drafts have no server
     * identity, but Store keys objectsMap by a numeric id and the mobile MMKV
     * layer rebuilds that key from the persisted object, so the id has to be a
     * real field on the draft.
     */
    id?: number
    modelType: 'room' | 'thread'
    modelId: number
    repliesToId: number | null
    text: string
    isUrgent: boolean
}

const MESSAGE_DRAFT_FIELDS = ['id', 'modelType', 'modelId', 'repliesToId', 'text', 'isUrgent']

/**
 * Holds unsent composer content per conversation so switching rooms (or being
 * remounted by RoomGate) doesn't discard what the user typed.
 *
 * Attachments are deliberately not drafted on either platform: they'd pull a
 * web-only File shape into shared code and can't be serialized for mobile
 * persistence.
 *
 * Cleared on send (CreateMessageController.postMessage) and on logout via
 * Store.clearAll(). On mobile only, persisted to encrypted MMKV by
 * StorePersistenceService; web keeps drafts in memory for the session only.
 */
class MessageDraftStore extends Store<MessageDraft> {
    constructor() {
        super(MESSAGE_DRAFT_FIELDS)
    }

    /**
     * Drafts are identified by a compound target rather than a server id, so
     * lookups go through findOne() instead of findById(). Volume is at most one
     * draft per conversation the user has typed in, so the scan is negligible.
     */
    getDraft(target: DraftTarget): MessageDraft | undefined {
        const repliesToId = target.repliesToId ?? null

        return this.findOne(d =>
            d.modelType === target.modelType
            && d.modelId === target.modelId
            && (d.repliesToId ?? null) === repliesToId
        )
    }

    /**
     * Upserts the draft for a target. Only text makes a draft worth keeping, so
     * toggling 'urgent' on an empty composer stores nothing and clearing the
     * text removes any existing draft.
     */
    setDraft(target: DraftTarget, draft: Pick<MessageDraft, 'text' | 'isUrgent'>) {
        const existing = this.getDraft(target)

        if (!draft.text?.trim()) {
            if (existing) this.deleteById(existing.id)
            return
        }

        // update() merges into the existing object, so write the whole shape.
        // Omitting isUrgent would leave a previously set flag in place.
        this.update({
            id: existing?.id ?? localId(),
            modelType: target.modelType,
            modelId: target.modelId,
            repliesToId: target.repliesToId ?? null,
            text: draft.text,
            isUrgent: draft.isUrgent
        })
    }

    clearDraft(target: DraftTarget) {
        const existing = this.getDraft(target)
        if (existing) this.deleteById(existing.id)
    }
}

decorate(injectable(), MessageDraftStore)

export { MessageDraftStore }
export type { DraftTarget, MessageDraft }
